/* 分析工具 — 分析報告 / 早晨報告（Analysis Report / Morning Report）
 *
 *  兩頁共用一套**卡片牆 + 整頁詳情**版面（2026-07-22 改版，原 list–detail 兩欄退役）：
 *    · 卡片牆：每份報告一張卡，正面顯示 title + 最多 4 條重點；點卡片整頁切換到詳情。
 *    · 詳情（整頁）：返回鈕 + 三分頁「重點整理／中文原文／原文」+（有權限才顯示）下載。
 *      「原文」= 上游 OCR 原始語言（content_en 欄位），來源可能本來就是中文，故不寫死「英文」。
 *
 *  兩頁差異僅由 `ReportWallPage({ morning })` 的 morning 旗標縮限（兩頁皆有日期導覽列、
 *  皆有右上「匯入並轉換」；盤前/盤中/盤後盤別分頁已於 2026-07-30 整條拆除）：
 *    · 兩頁共用同一 API，依 **uploader 分流**（2026-07-29，取代原 period=pre 時間縮限）：
 *      早晨報告 = uploaders 含 `MR_UPLOADER`（LINE 上傳者）的報告；其餘全歸分析報告。
 *    · 匯入差異：早晨報告頁匯入時 POST 帶 `morning=1` → 後端固定掛早晨上傳者
 *      （uploader=MR_UPLOADER 同值，後端寫死），報告落回早晨報告頁；
 *      分析報告頁匯入不帶 morning，報告歸分析報告頁。
 *
 *  後端 API（portal 代理 → 內網 ocr_auto OCR 服務，見 backend/routes_ocr_reports.py）：
 *    GET    /api/ocr-reports?date=YYYY-MM-DD → { reports: [ReportMeta] }
 *           date = 只回該日（後端轉上游 timestamp 前綴查詢，不受上游預設 30 筆截斷）；
 *           省略 = 不篩日期（上限 500 筆）。**換日期會重新打 API**（非純前端過濾）。
 *    GET    /api/ocr-reports/:id            → ReportMeta + { content_markdown, error_message, lang }
 *    POST   /api/ocr-reports  (multipart)   → { id, ids }   欄位：files（一或多個）、
 *           morning（選填 "1" = 早晨報告頁匯入，後端固定掛早晨上傳者）
 *    GET    /api/ocr-reports/:id/download   下載 markdown（帶 Authorization 或 ?token=）
 *
 *  ReportMeta：{ id, title, status, created_at, source_count, pages, tokens,
 *               summary, key_points, summary_pending, uploader, uploaders }
 *    status ∈ processing | completed | failed。
 *    uploader: str|null（上傳者，如 LINE user id）、uploaders: [str]（同檔多人上傳合併）。
 *    title 優先為上游 AI 摘要標題（可能英文），無摘要時 fallback 檔名推導。
 *    summary: str|null（AI 全文摘要）、key_points: [str]（AI 重點整理）、summary_pending: bool。
 *
 *  中/英原文：後端目前一份報告僅回單一 content_markdown（依 translate/lang 為繁中或英文原文），
 *  上游其實兩份都有（/api/ocr/result?lang=en|zh、靜態 .md / .zh.md）。詳情 UI 先把兩分頁做好，
 *  優先讀 report.content_zh / content_en（後端日後補），缺則以 content_markdown + lang 對應，
 *  另一語系顯示「待接」提示。
 *
 *  下載權限（外部分析帳號分級）：一般外部分析（analyst_lite）不可下載；高級外部分析（analyst）、
 *  non_staff、內部帳號可下載。前端以 `ocr_report.download` capability + isRestrictedAccount 判定
 *  是否顯示下載鈕；後端 download 端點同步 gate（capability 為 UI 層，非安全層）。
 */

const AR_STATUS_META = {
  processing: { label: '轉換中', tone: 'warn',   dot: true },
  completed:  { label: '已完成', tone: 'accent', dot: true },
  failed:     { label: '失敗',   tone: 'down',   dot: true },
};

/* Expressive（新版外殼）樣式 patch —— 一律以 uiStyle(bento, patch) 套用，Bento 輸出不變。
 * 寫成函式（render 時才讀 GxT），避免模組頂層抄值（plan-expressive-inner-pages.md §2(d)-2）。 */
function AR_GX_MENU() {
  return { border: 'none', borderRadius: GxT.radius.inner, boxShadow: GxT.shadow.pop, padding: 6 };
}
function AR_GX_MENU_ITEM() {
  return { borderRadius: GxT.radius.btnSm, padding: '10px 14px', minHeight: 40, fontWeight: 600 };
}
// 狀態提示框（轉換中 / 摘要生成中 → warn；失敗 → down）：tonal、無框、圓角 12、AA 前景色
function AR_GX_NOTE(kind) {
  const t = GxCore.tone(kind);
  return { background: t.bg, color: t.fg, border: 'none', borderLeft: 'none', borderRadius: GxT.radius.inner };
}
// 返回鈕：Expressive btnSm tonal 樣式（plan §4.1 P5）
function AR_GX_BACK() {
  return {
    fontFamily: GxT.font, fontSize: 13, fontWeight: 700, minHeight: 32, padding: '0 14px',
    border: 'none', borderRadius: GxT.radius.btnSm, background: GxT.color.pc, color: GxT.color.onPc,
  };
}

// 卡片牆每頁筆數 — 超過即分頁（上一頁／下一頁）。
const REPORTS_PER_PAGE = 20;

// 下載選項（語言 × 格式）— 單份詳情與批次下載共用。
const DL_OPTIONS = [
  { lang: 'zh', fmt: 'pdf', label: '中文報告 · PDF' },
  { lang: 'zh', fmt: 'md',  label: '中文報告 · Markdown' },
  { lang: 'en', fmt: 'pdf', label: '英文報告 · PDF' },
  { lang: 'en', fmt: 'md',  label: '英文報告 · Markdown' },
];

// 早晨報告上傳者（LINE user id）— uploaders 含此 id 的報告歸「早晨報告」頁，
// 其餘（含 portal「匯入並轉換」上傳、uploader 缺漏的舊 job）歸「分析報告」頁。
const MR_UPLOADER = 'Ud45da420194e3b4d50ee694346738ce7';

// 報告是否屬早晨報告 — 優先看 uploaders 清單（同檔多人上傳合併），fallback 單值 uploader。
function AR_isMorningReport(r) {
  const ups = Array.isArray(r.uploaders) && r.uploaders.length ? r.uploaders : [r.uploader];
  return ups.includes(MR_UPLOADER);
}

// created_at → 'YYYY-MM-DD'（本地日期，供日期篩選比對）；無效日期回 null 一律濾掉
function AR_dateStr(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d.toLocaleDateString('sv-SE') : null;
}
function AR_fmtTime(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false }) : '--:--';
}
function AR_fmtDateTime(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d)
    ? d.toLocaleString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
    : '';
}

// 日期 ± n 天：固定 T00:00:00 本地時間再加減，避免時區位移
function AR_shiftDate(ymd, delta) {
  const d = new Date(ymd + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return d.toLocaleDateString('sv-SE');
}

/* 日期導覽列 —— ‹ › 前後日切換、點日期展開日曆、「今天」快捷、「全部日期」切換。
 * value = 'YYYY-MM-DD'（'' = 全部日期）；不可選未來（› 在今天時 disabled、日曆 max 今天）。 */
function AR_DateNav({ value, onChange }) {
  const today = todayStr();
  const dateRef = React.useRef(null);

  // 點日期文字展開原生日曆；showPicker 需 user gesture（Chrome/Edge OK），失敗 fallback focus+click
  const openPicker = () => {
    const el = dateRef.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); el.click(); }
  };

  const d = value ? new Date(value + 'T00:00:00') : null;
  const label = d && !isNaN(d)
    ? d.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'short' })
    : '全部日期';
  const isToday = value === today;
  const isYesterday = value === AR_shiftDate(today, -1);

  const arrowStyle = (disabled) => uiStyle({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, padding: 0, border: 'none', borderRadius: 5,
    background: 'transparent', cursor: disabled ? 'default' : 'pointer',
    color: disabled ? HR.inkFaint : HR.inkSoft,
    fontFamily: HR.sans, fontSize: 20, lineHeight: 1,
  }, { width: 36, height: 36, borderRadius: GxT.radius.btnSm, color: disabled ? GxT.color.ol : GxT.color.on, opacity: disabled ? 0.5 : 1 });

  return (
    <div style={uiStyle({
      flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px', border: `1px solid ${HR.rule}`, borderRadius: 6, background: HR.surface,
    }, { gap: 4, padding: '2px 8px 2px 2px', border: 'none', borderRadius: GxT.radius.inner, background: GxT.color.card, flexWrap: 'wrap' })}>
      <button
        type="button" aria-label="前一天" disabled={!value}
        onClick={() => value && onChange(AR_shiftDate(value, -1))}
        style={arrowStyle(!value)}
      >‹</button>

      {/* 日期顯示（點擊展開日曆）；隱形 date input 疊在文字上 */}
      <span
        onClick={openPicker} role="button" aria-label="選擇日期"
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '2px 6px', cursor: 'pointer' }}
      >
        <span style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 700, color: HR.ink, whiteSpace: 'nowrap' },
          { fontWeight: GxT.weight.title, fontVariantNumeric: 'tabular-nums' })}>
          {label}
        </span>
        {isToday && <Pill tone="accent">今天</Pill>}
        {isYesterday && <Pill tone="accent">昨天</Pill>}
        <input
          ref={dateRef} type="date" value={value} max={today} tabIndex={-1}
          onChange={e => onChange(e.target.value)} aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, border: 0, padding: 0, cursor: 'pointer' }}
        />
      </span>

      <button
        type="button" aria-label="後一天" disabled={!value || value >= today}
        onClick={() => value && value < today && onChange(AR_shiftDate(value, 1))}
        style={arrowStyle(!value || value >= today)}
      >›</button>

      {!isToday && <Button variant="ghost" size="sm" onClick={() => onChange(today)}>今天</Button>}
      {value && <Button variant="ghost" size="sm" onClick={() => onChange('')}>全部日期</Button>}
    </div>
  );
}

/* 分析報告 / 早晨報告 共用卡片牆頁。morning 旗標縮限範圍（見檔頭）。 */
function ReportWallPage({ morning = false }) {
  const toast = useToast();
  const [reports, setReports] = React.useState(null);   // null = 尚未載入
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState('');
  const [dateSel, setDateSel] = React.useState(todayStr());  // 'YYYY-MM-DD'；'' = 全部日期（morning 不參與）
  const [selId, setSelId] = React.useState(null);
  const [showImport, setShowImport] = React.useState(false);
  const [page, setPage] = React.useState(0);   // 卡片牆分頁（0-based）
  const [picked, setPicked] = React.useState(() => new Set());   // 批次下載勾選的報告 id
  const [bulkDlOpen, setBulkDlOpen] = React.useState(false);     // 批次下載選單開合

  // 清單查詢帶所選日期（'' = 全部日期不帶）——換日期會重新打 API（refresh 隨 dateSel 重建），
  // 舊資料（超出上游預設 30 筆截斷的）才撈得到；輪詢沿用同一查詢範圍。
  const refresh = React.useCallback(() => {
    api('GET', '/ocr-reports' + (dateSel ? `?date=${dateSel}` : ''))
      .then(raw => setReports(Array.isArray(raw) ? raw : (raw && raw.reports) || []))
      .catch(e => {
        // 後端尚未實作 → 顯示空狀態，不彈錯（404）；其他錯誤才提示
        if (!e || e.status !== 404) toast((e && e.detail) || '報告載入失敗', 'error');
        setReports([]);
      })
      .finally(() => setLoading(false));
  }, [toast, dateSel]);

  // 初載 + 換日期重查（重查時先進 loading 骨架，避免短暫閃「這天沒有報告」空狀態）
  React.useEffect(() => { setLoading(true); refresh(); }, [refresh]);

  // 有報告在轉換中或摘要生成中 → 每 5 秒輪詢（AI 標題生成後清單自動更新）
  React.useEffect(() => {
    const hasProcessing = (reports || []).some(r => r.status === 'processing' || r.summary_pending);
    if (!hasProcessing) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [reports, refresh]);

  // 篩選條件改變（搜尋 / 日期）→ 回到第 1 頁 + 清掉勾選（避免選到看不見的項目）
  React.useEffect(() => { setPage(0); setPicked(new Set()); setBulkDlOpen(false); }, [q, dateSel]);

  const all = reports || [];
  const query = q.trim().toLowerCase();
  const searched = all
    .filter(r => !query || ((r.title || '') + (r.id || '') + (r.summary || '')).toLowerCase().includes(query))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  // 依 uploader 分流（互斥切分）：早晨報告 = 早晨上傳者（MR_UPLOADER）的報告、不限時段；
  // 分析報告 = 其餘全部（含 portal 匯入、uploader 缺漏的舊 job）。
  const scoped = morning ? searched.filter(AR_isMorningReport) : searched.filter(r => !AR_isMorningReport(r));
  // 兩頁皆用日期導覽選取（'' = 全部日期）。日期已由後端查詢範圍縮限（?date=），
  // 本地再過濾一次是保險（上游舊版忽略 date 參數時仍回混雜日期，靠這層維持正確）。
  const effDate = dateSel;
  const dated = scoped.filter(r => !effDate || AR_dateStr(r.created_at) === effDate);
  const filtered = dated;

  // 分頁：每頁 REPORTS_PER_PAGE 篇；curPage 對 totalPages 夾住（篩選縮小後不會停在空頁）
  const totalPages = Math.max(1, Math.ceil(filtered.length / REPORTS_PER_PAGE));
  const curPage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(curPage * REPORTS_PER_PAGE, curPage * REPORTS_PER_PAGE + REPORTS_PER_PAGE);

  const processingCount = all.filter(r => r.status === 'processing').length;
  const detailReport = selId ? dated.find(r => r.id === selId) : null;

  // 下載權限（同詳情浮層）：一般外部分析（analyst_lite）不可下載 → 不顯示勾選 / 批次列
  const _user = (typeof window !== 'undefined' && window._USER) || null;
  const canDownload = (typeof window.hasCapability === 'function' && window.hasCapability(_user, 'ocr_report.download'))
    || (typeof window.isRestrictedAccount === 'function' ? !window.isRestrictedAccount(_user) : true);

  const togglePick = React.useCallback((id) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const pickPage = () => setPicked(prev => new Set([...prev, ...pageItems.map(r => r.id)]));
  const clearPicks = () => { setPicked(new Set()); setBulkDlOpen(false); };
  const doBatchDownload = (dlLang, fmt) => {
    const arr = Array.from(picked);
    if (!arr.length) return;
    const token = (typeof getToken === 'function' && getToken()) || '';
    const url = `${API_BASE}/ocr-reports/download-batch?ids=${encodeURIComponent(arr.join(','))}&lang=${dlLang}&fmt=${fmt}&token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
    setBulkDlOpen(false);
  };

  const header = (
    <PageHeader
      eyebrow={morning ? '分析工具 · 早晨報告' : '分析工具 · 研究報告庫'}
      title={morning ? '早晨報告' : '研究報告庫'}
      italic={morning ? 'Morning Reports' : 'Analysis Reports'}
      action={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {processingCount > 0 && (
            <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.warn }}>
              {processingCount} 份轉換中…
            </span>
          )}
          <Button variant="ghost" size="sm" icon="refresh" onClick={() => { setLoading(true); refresh(); }}>重新整理</Button>
          <Button variant="primary" size="sm" icon="upload" onClick={() => setShowImport(true)}>匯入並轉換</Button>
        </span>
      }
    />
  );

  // ── 卡片牆（詳情改為浮層 overlay，蓋在卡片牆上；見 fragment 末端 detailReport）──
  return (
    <>
      {header}

      {/* 工具列：日期導覽（兩頁皆有）+ 搜尋 + 份數 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 18px', flexWrap: 'wrap' }}>
        <AR_DateNav value={dateSel} onChange={setDateSel} />
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 400 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: HR.inkFaint, display: 'flex', pointerEvents: 'none' }}>
            <Icon name="search" size={15} />
          </span>
          <input
            type="text" value={q} onChange={e => setQ(e.target.value)}
            placeholder="搜尋報告標題、編號…" aria-label={morning ? '搜尋早晨報告' : '搜尋研究報告庫'}
            style={inputStyle({ paddingLeft: 34, paddingRight: q ? 32 : 11 })}
          />
          {q && (
            <button onClick={() => setQ('')} aria-label="清除搜尋" style={{
              position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
              border: 'none', background: 'none', cursor: 'pointer', color: HR.inkFaint, display: 'flex', padding: 3, borderRadius: 4,
            }}>
              <Icon name="x" size={13} />
            </button>
          )}
        </div>
        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
          {(effDate ? effDate.replace(/-/g, '/') : '全部日期')} · {filtered.length} 份
        </span>
      </div>

      {/* 批次下載列 — 有勾選才出現（只在可下載帳號顯示） */}
      {canDownload && picked.size > 0 && (
        <div style={uiStyle({
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          margin: '0 0 16px', padding: '10px 14px',
          background: HR.accentSoft, border: `1px solid ${GxCore.alpha(HR.accent, '33')}`, borderRadius: 8,
        }, { padding: '10px 12px 10px 18px', background: GxT.color.pc, border: 'none', borderRadius: GxT.radius.inner })}>
          <span style={uiStyle({ fontFamily: HR.sans, fontSize: FontSizes.sm, fontWeight: 700, color: HR.accent },
            { fontWeight: GxT.weight.title, color: GxT.color.onPc })}>已選 {picked.size} 篇</span>
          <Button variant="ghost" size="sm" onClick={pickPage}>本頁全選</Button>
          <Button variant="ghost" size="sm" onClick={clearPicks}>清除</Button>
          <span style={{ flex: 1 }} />
          <div style={{ position: 'relative' }}>
            <Button variant="primary" size="sm" icon="download" onClick={() => setBulkDlOpen(o => !o)}>下載選取 ▾</Button>
            {bulkDlOpen && (
              <>
                <div onClick={() => setBulkDlOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                <div style={uiStyle({
                  position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 31,
                  background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(14,20,17,0.16)', minWidth: 210, overflow: 'hidden', padding: '4px 0',
                }, AR_GX_MENU())}>
                  {DL_OPTIONS.map(o => (
                    <button
                      key={o.lang + o.fmt} type="button" onClick={() => doBatchDownload(o.lang, o.fmt)}
                      onMouseEnter={e => { e.currentTarget.style.background = HR.surfaceAlt; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                      style={uiStyle({
                        display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px',
                        border: 0, background: 'none', cursor: 'pointer',
                        fontFamily: HR.sans, fontSize: FontSizes.sm, color: HR.ink, whiteSpace: 'nowrap',
                      }, AR_GX_MENU_ITEM())}
                    >{o.label}（{picked.size} 篇 ZIP）</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={uiStyle({ background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 10, padding: '18px 20px' },
              { border: 'none', borderRadius: GxT.radius.card })}>
              <SkeletonText lines={5} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="file-text"
          title={all.length === 0 ? (morning ? '尚無早晨報告' : '尚無研究報告') : (morning ? '這天沒有符合的報告' : '找不到符合的報告')}
          hint={morning
            ? all.length === 0
              ? '此頁只顯示早晨報告來源上傳的報告；可點「匯入並轉換」上傳 PDF / 圖片，會自動掛到早晨報告。其他來源的報告請到「研究報告庫」頁查看。'
              : '此頁只顯示早晨報告來源上傳的報告；試試其他日期或關鍵字，其他來源的報告請到「研究報告庫」頁查看。'
            : all.length === 0
              ? '點右上「匯入並轉換」上傳 PDF / 圖片產生報告。'
              : dateSel
                ? '這一天沒有符合的報告，試試其他日期。'
                : '試試其他關鍵字。'}
          action={all.length === 0 ? <Button variant="primary" icon="upload" onClick={() => setShowImport(true)}>匯入並轉換</Button> : null}
        />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16, alignItems: 'stretch' }}>
            {pageItems.map(r => (
              <ReportCard
                key={r.id} report={r} onOpen={() => setSelId(r.id)}
                selectable={canDownload} picked={picked.has(r.id)} onTogglePick={togglePick}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 24 }}>
              <Button variant="ghost" size="sm" disabled={curPage <= 0} onClick={() => setPage(curPage - 1)}>‹ 上一頁</Button>
              <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkSoft }}>
                第 {curPage + 1} / {totalPages} 頁
              </span>
              <Button variant="ghost" size="sm" disabled={curPage >= totalPages - 1} onClick={() => setPage(curPage + 1)}>下一頁 ›</Button>
            </div>
          )}
        </>
      )}

      {showImport && (
        <ImportReportModal
          morning={morning}
          onCancel={() => setShowImport(false)}
          onCreated={(id) => { setShowImport(false); refresh(); if (id) setSelId(id); }}
        />
      )}

      {/* 詳情浮層 — 點卡片彈出、蓋在卡片牆上；backdrop 點擊 / Esc / 返回鈕關閉 */}
      {detailReport && (
        <ReportDetail
          key={selId} reportId={selId} morning={morning}
          onClose={() => setSelId(null)}
          onChanged={refresh}
          onDeleted={() => { setSelId(null); setPicked(new Set()); refresh(); }}
        />
      )}
    </>
  );
}

/* 報告卡片 — 正面：時間/狀態 → 標題（2 行）→ 最多 4 條重點 → #編號·來源 + 閱讀原文。
 * selectable：顯示批次下載勾選框（右上角）；勾選框點擊不會開詳情。 */
function ReportCard({ report: r, onOpen, selectable = false, picked = false, onTogglePick }) {
  const smeta = AR_STATUS_META[r.status] || AR_STATUS_META.processing;
  const kps = (r.key_points || []).slice(0, 4);   // 固定最多 4 條
  const done = r.status === 'completed';
  // Expressive：卡片無框（透明框保留 hover／勾選時的主色框線位置，不跳版）
  const baseBorder = picked ? HR.accent : uiPick(HR.rule, 'transparent');

  return (
    <div
      role="button" tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 6px 22px rgba(14,20,17,.09)';
        e.currentTarget.style.borderColor = HR.accent;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = baseBorder;
        e.currentTarget.style.transform = 'none';
      }}
      style={uiStyle({
        position: 'relative',
        background: HR.surface, border: `1px solid ${baseBorder}`, borderRadius: 10,
        padding: '18px 20px 15px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', minHeight: 210,
        boxShadow: picked ? `0 0 0 1px ${HR.accent}` : 'none',
        transition: 'box-shadow 140ms, border-color 140ms, transform 140ms',
      }, { borderRadius: GxT.radius.card, padding: '20px 20px 16px' })}
    >
      {selectable && (
        <label
          onClick={e => e.stopPropagation()}
          title="選取此報告加入批次下載"
          style={{ position: 'absolute', top: 12, right: 12, display: 'inline-flex', cursor: 'pointer', zIndex: 1 }}
        >
          <input
            type="checkbox" checked={picked} onChange={() => onTogglePick && onTogglePick(r.id)}
            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: HR.accent }}
          />
        </label>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingRight: selectable ? 24 : 0 }}>
        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{AR_fmtTime(r.created_at)}</span>
        {!done && <Pill tone={smeta.tone} dot={smeta.dot}>{smeta.label}</Pill>}
      </div>

      <h3 style={uiStyle({
        fontFamily: HR.serif, fontSize: FontSizes.lg, lineHeight: 1.32, fontWeight: 700, color: HR.ink,
        letterSpacing: '-0.005em', margin: '0 0 12px',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }, { fontSize: 20, fontWeight: GxT.weight.title, letterSpacing: '-0.01em' })}>{r.title || '(未命名報告)'}</h3>

      {kps.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: '0 0 auto', padding: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
          {kps.map((p, i) => (
            <li key={i} style={{
              position: 'relative', paddingLeft: 16, fontFamily: HR.serif, fontSize: FontSizes.sm, lineHeight: 1.5, color: HR.inkSoft,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              <span style={{ position: 'absolute', left: 0, top: 8, width: 5, height: 5, borderRadius: '50%', background: HR.accent }} />
              {p}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{
          margin: '0 0 auto', fontFamily: HR.serif, fontSize: FontSizes.sm, lineHeight: 1.6, color: HR.inkFaint,
          display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {r.summary
            ? r.summary
            : (r.summary_pending || r.status === 'processing') ? '重點整理生成中…' : '（尚無重點整理）'}
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginTop: 14, paddingTop: 12, borderTop: `1px solid ${HR.ruleSoft}`,
        fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint,
      }}>
        <span>#{r.id} · 來源 {r.source_count != null ? `${r.source_count} 檔` : '—'}</span>
        <span style={uiStyle({ fontFamily: HR.sans, fontWeight: 600, fontSize: FontSizes.trading_xl, color: HR.accent },
          { fontWeight: 700, fontSize: 13 })}>閱讀原文 →</span>
      </div>
    </div>
  );
}

/* 報告詳情（整頁）— 返回卡片牆 + 三分頁「重點整理／中文原文／原文」+（有權限）下載。 */
function ReportDetail({ reportId, morning = false, onClose, onChanged, onDeleted }) {
  const toast = useToast();
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState(null);   // null = 依載入後內容決定預設分頁
  const [dlOpen, setDlOpen] = React.useState(false);   // 下載選單開合
  const [editing, setEditing] = React.useState(false);   // 編輯 modal
  const [confirmDel, setConfirmDel] = React.useState(false);
  const [delBusy, setDelBusy] = React.useState(false);
  const bodyRef = React.useRef(null);

  const switchTab = React.useCallback((next) => {
    setTab(next);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);

  const load = React.useCallback(() => {
    api('GET', `/ocr-reports/${reportId}`)
      .then(r => setReport(r))
      .catch(e => toast((e && e.detail) || '報告載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [reportId, toast]);

  React.useEffect(() => { setLoading(true); load(); }, [load]);

  // 轉換中或摘要生成中 → 輪詢直到完成
  React.useEffect(() => {
    if (!report || (report.status !== 'processing' && !report.summary_pending)) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [report, load]);

  // Esc 關閉浮層（編輯 / 刪除確認開著時不關，讓 Esc 由那層自己處理）
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !editing && !confirmDel) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editing, confirmDel]);

  const doDelete = async () => {
    setDelBusy(true);
    try {
      await api('DELETE', `/ocr-reports/${reportId}`);
      toast('報告已刪除', 'ok');
      setConfirmDel(false);
      if (onDeleted) onDeleted();
    } catch (e) {
      toast((e && e.detail) || '刪除失敗', 'error');
    } finally {
      setDelBusy(false);
    }
  };

  const meta = report ? (AR_STATUS_META[report.status] || AR_STATUS_META.processing) : null;
  const hasSummary = !!(report && (report.summary || (report.key_points || []).length));
  // 中/英原文：優先讀後端未來欄位 content_zh/content_en，缺則以 content_markdown + lang 對應
  const zhContent = report && (report.content_zh || (report.lang === 'zh' ? report.content_markdown : null));
  const enContent = report && (report.content_en || (report.lang === 'en' ? report.content_markdown : null));
  const effTab = tab || (hasSummary ? '重點整理' : (zhContent ? '中文原文' : '原文'));

  // 下載權限：一般外部分析（analyst_lite）不可下載；高級外部分析 / non_staff / 內部帳號可下載。
  // 前端以 ocr_report.download capability + isRestrictedAccount 判定（後端 download 端點同步 gate）。
  const _user = (typeof window !== 'undefined' && window._USER) || null;
  const isInternal = (typeof window.isRestrictedAccount === 'function' ? !window.isRestrictedAccount(_user) : true);
  const canDownload = !!report && report.status === 'completed' && (
    (typeof window.hasCapability === 'function' && window.hasCapability(_user, 'ocr_report.download')) || isInternal
  );
  // 編輯 / 刪除限內部帳號（對齊後端 _can_manage_reports；分析報告的管理動作）
  const canManage = isInternal;

  // 選取式下載：語言（zh|en）× 格式（md|pdf）
  const doDownload = (dlLang, fmt) => {
    if (!report) return;
    const token = (typeof getToken === 'function' && getToken()) || '';
    const url = `${API_BASE}/ocr-reports/${report.id}/download?lang=${dlLang}&fmt=${fmt}&token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
    setDlOpen(false);
  };

  // 某語系原文尚無內容時的占位（生成中 vs 後端待接）
  const langPlaceholder = (label) => (
    <div style={{ marginTop: 22 }}>
      {report.status === 'processing' || report.summary_pending ? (
        <div style={uiStyle({
          padding: '16px 18px', background: '#FAEFD8', border: `1px solid ${GxCore.alpha(HR.warn, '33')}`,
          borderLeft: `3px solid ${HR.warn}`, borderRadius: 6, color: HR.warn, fontFamily: HR.serif, fontSize: FontSizes.md,
        }, AR_GX_NOTE('warn'))}>
          ⏳ {label}生成中…（畫面每 4 秒自動更新）
        </div>
      ) : (
        <EmptyState
          icon="file-text"
          title={`${label}尚未提供`}
          hint="後端接上多語系內容後，這裡會顯示對應原文；目前可切換另一分頁或下載完整檔案。"
        />
      )}
    </div>
  );

  // 浮層內容：載入中 → 骨架；找不到 → 提示；否則 → 工具列 + 內文
  let sheetInner;
  if (loading) {
    sheetInner = (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={uiStyle({ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt },
          { borderBottom: `1px solid ${GxT.color.olv}`, background: GxT.color.s1 })}>
          <button type="button" onClick={onClose} style={uiStyle({ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkSoft, padding: '5px 10px', border: `1px solid ${HR.rule}`, borderRadius: 6, background: HR.surface }, AR_GX_BACK())}>‹ 返回</button>
        </div>
        <div style={{ padding: '34px 28px' }}><SkeletonText lines={6} /></div>
      </div>
    );
  } else if (!report) {
    sheetInner = (
      <div style={{ padding: '48px 28px', textAlign: 'center', color: HR.inkFaint, fontSize: FontSizes.xs }}>
        找不到這份報告。<span style={{ marginLeft: 10, cursor: 'pointer', color: HR.accent }} onClick={onClose}>關閉</span>
      </div>
    );
  } else {
    sheetInner = (
      <>
      {/* sticky 工具列：返回／狀態／編號·頁數·時間／下載 */}
      <div style={uiStyle({
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 20px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
      }, { gap: 10, borderBottom: `1px solid ${GxT.color.olv}`, background: GxT.color.s1 })}>
        <button
          type="button" onClick={onClose}
          style={uiStyle({
            display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
            fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkSoft,
            padding: '5px 10px', border: `1px solid ${HR.rule}`, borderRadius: 6, background: HR.surface,
          }, AR_GX_BACK())}
        >‹ 返回</button>
        <Pill tone={meta.tone} dot={meta.dot}>{meta.label}</Pill>
        <span style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
          #{report.id} · 頁數 {report.pages != null ? report.pages : '—'} · {AR_fmtDateTime(report.created_at)}
        </span>
        <span style={{ flex: 1 }} />
        {canManage && <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(true)}>編輯</Button>}
        {canManage && <Button size="sm" variant="ghost" icon="trash" onClick={() => setConfirmDel(true)}>刪除</Button>}
        {canDownload && (
          <div style={{ position: 'relative' }}>
            <Button size="sm" variant="ghost" icon="download" onClick={() => setDlOpen(o => !o)}>下載 ▾</Button>
            {dlOpen && (
              <>
                {/* 點外面關閉（fixed 覆蓋視窗，不影響報告浮層——sheet 有 stopPropagation） */}
                <div onClick={() => setDlOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000 }} />
                <div style={uiStyle({
                  position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 1001,
                  background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(14,20,17,0.16)', minWidth: 190, overflow: 'hidden', padding: '4px 0',
                }, AR_GX_MENU())}>
                  {DL_OPTIONS.map(o => (
                    <button
                      key={o.lang + o.fmt} type="button" onClick={() => doDownload(o.lang, o.fmt)}
                      onMouseEnter={e => { e.currentTarget.style.background = HR.surfaceAlt; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                      style={uiStyle({
                        display: 'block', width: '100%', textAlign: 'left', padding: '9px 16px',
                        border: 0, background: 'none', cursor: 'pointer',
                        fontFamily: HR.sans, fontSize: FontSizes.sm, color: HR.ink, whiteSpace: 'nowrap',
                      }, AR_GX_MENU_ITEM())}
                    >{o.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <button
          type="button" onClick={onClose} aria-label="關閉"
          style={uiStyle({ display: 'inline-flex', border: 'none', background: 'none', cursor: 'pointer', color: HR.inkFaint, padding: 4, borderRadius: 4 },
            { width: 40, height: 40, padding: 0, alignItems: 'center', justifyContent: 'center', borderRadius: GxT.radius.btn, color: GxT.color.on })}
        ><Icon name="x" size={uiPick(16, 18)} /></button>
      </div>

      {/* 內文捲動區（置中舒適行寬 720） */}
      <div ref={bodyRef} className="ar-readbody" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '34px 28px 48px' }}>
        <article style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ fontSize: FontSizes.xs, fontWeight: 700, letterSpacing: '0.04em', color: HR.accent, marginBottom: 12 }}>
            ● {morning ? '早晨報告' : '研究報告庫'}
          </div>
          <h2 style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, lineHeight: 1.3, fontWeight: 700, color: HR.ink, letterSpacing: '-0.01em', margin: 0 },
            { fontWeight: GxT.weight.display, lineHeight: 1.2, letterSpacing: '-0.02em' })}>
            {report.title || '(未命名報告)'}
          </h2>

          {/* 分頁列：重點整理／中文原文／原文 — 僅在已完成時顯示（Expressive：M3 chips 型分頁） */}
          {report.status === 'completed' && (
            <div style={uiStyle({ display: 'flex', gap: 4, marginTop: 16, borderBottom: `1px solid ${HR.rule}` },
              { gap: 8, marginTop: 18, borderBottom: 'none', flexWrap: 'wrap' })}>
              {['重點整理', '中文原文', '原文'].map(key => {
                const cur = effTab === key;
                return (
                  <button
                    key={key} type="button" onClick={() => switchTab(key)}
                    style={uiStyle({
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      fontFamily: HR.sans, fontSize: FontSizes.sm, fontWeight: 600,
                      color: cur ? HR.ink : HR.inkFaint,
                      padding: '9px 16px 11px', border: 0, background: 'none', cursor: 'pointer',
                      borderBottom: `2px solid ${cur ? HR.accent : 'transparent'}`, marginBottom: -1,
                    }, {
                      fontWeight: 700, minHeight: 40, padding: '0 16px', borderRadius: GxT.radius.btn,
                      borderBottom: 'none', marginBottom: 0,
                      background: cur ? GxT.color.pc : GxT.color.s2, color: cur ? GxT.color.onPc : GxT.color.onV,
                    })}
                  >{key}</button>
                );
              })}
            </div>
          )}

          {report.status === 'processing' ? (
            <div style={uiStyle({
              marginTop: 22, padding: '16px 18px', background: '#FAEFD8',
              border: `1px solid ${GxCore.alpha(HR.warn, '33')}`, borderLeft: `3px solid ${HR.warn}`, borderRadius: 6,
              color: HR.warn, fontFamily: HR.serif, fontSize: FontSizes.md,
            }, AR_GX_NOTE('warn'))}>
              ⏳ API 正在解析來源檔案並產生報告…（畫面每 4 秒自動更新）
            </div>
          ) : report.status === 'failed' ? (
            <div style={uiStyle({
              marginTop: 22, padding: '16px 18px', background: '#F4DAD8',
              border: `1px solid ${GxCore.alpha(HR.down, '33')}`, borderRadius: 6,
              color: HR.down, fontFamily: HR.mono, fontSize: FontSizes.trading_md,
            }, AR_GX_NOTE('down'))}>
              ⚠ 轉換失敗{report.error_message ? `：${report.error_message}` : ''}
            </div>
          ) : effTab === '重點整理' ? (
            hasSummary ? (
              <div style={{ marginTop: 22 }}>
                {report.summary && (
                  <p style={{ margin: 0, fontFamily: HR.serif, fontSize: FontSizes.md, lineHeight: 1.9, color: HR.ink }}>
                    {report.summary}
                  </p>
                )}
                {(report.key_points || []).length > 0 && (
                  <ul style={{ margin: report.summary ? '20px 0 0' : 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {report.key_points.map((p, i) => (
                      <li key={i} style={{ fontFamily: HR.serif, fontSize: FontSizes.md, lineHeight: 1.7, color: HR.ink }}>{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : report.summary_pending ? (
              <div style={uiStyle({
                marginTop: 22, padding: '16px 18px', background: '#FAEFD8',
                border: `1px solid ${GxCore.alpha(HR.warn, '33')}`, borderLeft: `3px solid ${HR.warn}`, borderRadius: 6,
                color: HR.warn, fontFamily: HR.serif, fontSize: FontSizes.md,
              }, AR_GX_NOTE('warn'))}>
                ⏳ 摘要生成中…（畫面每 4 秒自動更新，可先切原文閱讀）
              </div>
            ) : (
              <div style={{ marginTop: 22 }}>
                <EmptyState icon="file-text" title="重點整理尚未就緒" hint="API 重點整理功能上線後，將在此顯示本篇報告的結論、評等與核心觀點。" />
              </div>
            )
          ) : effTab === '中文原文' ? (
            zhContent ? (
              <div style={{ marginTop: 22 }}><MarkdownView className="ar-markdown" markdown={zhContent} /></div>
            ) : langPlaceholder('中文翻譯')
          ) : (
            enContent ? (
              <div style={{ marginTop: 22 }}><MarkdownView className="ar-markdown" markdown={enContent} /></div>
            ) : langPlaceholder('原文')
          )}
        </article>
      </div>
      </>
    );
  }

  return (
    <>
      <div
        className="hr-modal-overlay"
        onClick={onClose}
        style={uiStyle({
          position: 'fixed', inset: 0, background: 'rgba(14,20,17,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '40px 20px', overflowY: 'auto',
        }, { background: 'rgba(14,20,17,.32)' })}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={uiStyle({
            width: '100%', maxWidth: 880, background: HR.surface, borderRadius: 10,
            boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
            display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 80px)', overflow: 'hidden',
          }, { borderRadius: GxT.radius.modal, boxShadow: GxT.shadow.modal, fontFamily: GxT.font })}
        >
          {sheetInner}
        </div>
      </div>

      {editing && report && (
        <ReportEditModal
          reportId={reportId} report={report} zhContent={zhContent} enContent={enContent}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); if (onChanged) onChanged(); }}
        />
      )}
      <ConfirmDialog
        open={confirmDel} danger busy={delBusy}
        title="刪除報告"
        message={report ? `確定刪除「${report.title || '(未命名報告)'}」？此動作會刪除整份報告（含所有產物），無法復原。` : ''}
        onCancel={() => setConfirmDel(false)} onConfirm={doDelete}
      />
    </>
  );
}

/* 編輯報告 — 手動校正標題 / 中英內文（Markdown），送 PATCH /ocr-reports/:id（→ 上游 update）。 */
function ReportEditModal({ reportId, report, zhContent, enContent, onClose, onSaved }) {
  const toast = useToast();
  const [title, setTitle] = React.useState(report.title || '');
  const [zh, setZh] = React.useState(zhContent || '');
  const [en, setEn] = React.useState(enContent || '');
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    const body = {};
    if (title !== (report.title || '')) body.title = title;
    if (zh !== (zhContent || '')) body.markdown_zh = zh;
    if (en !== (enContent || '')) body.markdown = en;
    if (!Object.keys(body).length) { toast('沒有變更', 'error'); return; }
    setBusy(true);
    try {
      await api('PATCH', `/ocr-reports/${reportId}`, body);
      toast('已儲存', 'ok');
      onSaved();
    } catch (e) {
      toast((e && e.detail) || '儲存失敗', 'error');
    } finally {
      setBusy(false);
    }
  };

  const taStyle = uiStyle({
    width: '100%', minHeight: 180, resize: 'vertical', boxSizing: 'border-box',
    fontFamily: HR.mono, fontSize: FontSizes.trading_xl, lineHeight: 1.6,
    padding: '10px 12px', border: `1px solid ${HR.rule}`, borderRadius: 6, color: HR.ink, background: HR.surface,
  }, {
    // outlined field (= hr-shell inputStyle under GX); hover / focus ring come from gx-css-pages
    border: `1px solid ${GxT.color.ol}`, borderRadius: GxT.radius.input, background: GxT.color.card,
    fontSize: 13,
  });
  const labelStyle = uiStyle({ display: 'block', fontSize: FontSizes.trading_lg, color: HR.inkSoft, marginBottom: 5 },
    { fontSize: 13, fontWeight: 700, color: GxT.color.onV, marginBottom: 6 });

  return (
    <Modal open onClose={onClose} title="編輯報告" subtitle="Edit Report" size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>取消</Button>
        <Button variant="primary" onClick={save} disabled={busy}>{busy ? '儲存中…' : '儲存'}</Button>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>標題</label>
          <input value={title} onChange={e => setTitle(e.target.value)} style={inputStyle()} />
        </div>
        <div>
          <label style={labelStyle}>中文翻譯（Markdown）</label>
          <textarea value={zh} onChange={e => setZh(e.target.value)} style={taStyle} placeholder="（無中文內容）" />
        </div>
        <div>
          <label style={labelStyle}>原文（Markdown）</label>
          <textarea value={en} onChange={e => setEn(e.target.value)} style={taStyle} placeholder="（無原文內容）" />
        </div>
        <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
          儲存後會覆蓋上游對應語言的內容；未變更的欄位不送出。
        </div>
      </div>
    </Modal>
  );
}

/* 匯入並轉換 — 兩頁共用。morning=true（早晨報告頁）時 POST 帶 morning=1，
 * 後端固定掛早晨上傳者 uploader，報告落回早晨報告頁（uploader 值後端寫死，前端不傳 id）。 */
function ImportReportModal({ morning = false, onCancel, onCreated }) {
  const toast = useToast();
  const [files, setFiles] = React.useState([]);
  const [drag, setDrag] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const inputRef = React.useRef(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setFiles(prev => [...prev, ...incoming]);
  };
  const removeAt = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const fmtSize = (n) => n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
  const extOf = (name) => (name.split('.').pop() || '').toUpperCase().slice(0, 4);

  const submit = async () => {
    if (!files.length) { toast('請先選擇至少一個檔案', 'error'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      if (morning) fd.append('morning', '1');   // 早晨報告頁匯入 → 後端固定掛早晨上傳者
      const token = (typeof getToken === 'function' && getToken()) || '';
      const res = await fetch(`${API_BASE}/ocr-reports`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: fd,
      });
      const text = await res.text();
      let data = null; if (text) { try { data = JSON.parse(text); } catch { data = { raw: text }; } }
      if (!res.ok) throw { status: res.status, detail: (data && data.detail) || `HTTP ${res.status}` };
      toast('已開始轉換', 'ok');
      onCreated(data && data.id);
    } catch (e) {
      toast((e && e.detail) || '匯入失敗', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onCancel} title="匯入並轉換報告" subtitle="Import & Convert" size="md"
      footer={<>
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
        <Button variant="primary" onClick={submit} disabled={submitting || !files.length}>
          {submitting ? '上傳中…' : '開始轉換'}
        </Button>
      </>}>
      <input
        ref={inputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,image/*"
        style={{ display: 'none' }}
        onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
      />
      <div
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        style={uiStyle({
          border: `1.5px dashed ${drag ? HR.accent : HR.rule}`, borderRadius: 10,
          padding: '34px 20px', textAlign: 'center', cursor: 'pointer',
          background: drag ? HR.accentSoft : HR.surfaceAlt, color: drag ? HR.accent : HR.inkFaint,
          transition: 'all 120ms',
        }, {
          border: `1.5px dashed ${drag ? GxT.color.p : GxT.color.ol}`, borderRadius: GxT.radius.card,
          background: drag ? GxT.color.pc : GxT.color.s2, color: drag ? GxT.color.onPc : GxT.color.onV,
        })}>
        <div style={{ color: HR.accent, marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Icon name="upload" size={30} /></div>
        <div style={{ fontSize: FontSizes.xs, color: HR.inkSoft }}>拖曳 PDF / 圖片到這裡，或點擊選取</div>
        <div style={{ fontSize: FontSizes.trading_lg, marginTop: 6 }}>支援一次匯入多檔 · PDF、PNG、JPG、TIFF</div>
      </div>

      {files.length > 0 && (
        <div style={{ marginTop: 13, display: 'flex', flexDirection: 'column', gap: 7 }}>
          {files.map((f, i) => (
            <div key={i} style={uiStyle({
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 12px', background: HR.surfaceAlt, border: `1px solid ${HR.rule}`, borderRadius: 7, fontSize: FontSizes.trading_xl,
            }, { padding: '10px 12px', background: GxT.color.s2, border: 'none', borderRadius: GxT.radius.inner, fontSize: 13 })}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={uiStyle({ fontFamily: HR.mono, fontSize: FontSizes.trading_md, background: HR.accentSoft, color: HR.accent, padding: '2px 6px', borderRadius: 4 },
                  { borderRadius: GxT.radius.chipSm, fontWeight: 700 })}>{extOf(f.name)}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                <span style={{ color: HR.inkFaint, flexShrink: 0 }}>{fmtSize(f.size)}</span>
              </span>
              <button onClick={() => removeAt(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: HR.inkFaint, display: 'flex' }}><Icon name="x" size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
        {morning ? '將自動轉換為「早晨報告」格式。' : '將自動轉換為「研究報告」格式。'}
      </div>
    </Modal>
  );
}

/* 對外頁面元件 — 兩者共用 ReportWallPage，僅 morning 旗標不同。 */
function AnalysisReportPage() {
  return <ReportWallPage />;
}
function MorningReportPage() {
  return <ReportWallPage morning />;
}

window.AnalysisReportPage = AnalysisReportPage;
window.MorningReportPage = MorningReportPage;
